import {
  AppBar,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  ArrowBack,
  ChatBubbleOutline,
  Home as HomeIcon,
  Menu as MenuIcon,
  PeopleAlt as PeopleAltIcon,
  Storage as StorageIcon,
  TableChart as TableChartIcon,
  Settings,
  AccountCircle,
} from "@mui/icons-material";
import { useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { LOGO_URL } from "config";
import { useAppSelector } from "features/store";

interface ClientViewHeaderProps {
  onBackToUserManagement: () => void;
}

export default function ClientViewHeader({
  onBackToUserManagement,
}: ClientViewHeaderProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { userId } = useParams<{ userId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const clientView = useAppSelector((state) => state.clientView);

  const basePath = `/client-view/${userId ?? ""}`;
  const storagePath = `${basePath}/data-storage`;
  const isLandingPath =
    location.pathname === basePath || location.pathname === basePath + "/";
  const isDataStoragePath = location.pathname === storagePath;
  const isActive = (path: string) => {
    if (path === basePath || path === basePath + "/")
      return location.pathname === path || location.pathname === path + "/";
    return location.pathname.startsWith(path);
  };

  const navBtnSx = (active: boolean) => ({
    textTransform: "none" as const,
    borderRadius: 999,
    px: 1.5,
    fontWeight: active ? 800 : 600,
    color: active ? "primary.main" : "text.primary",
    bgcolor: active ? "rgba(31,93,107,0.08)" : "transparent",
    "&:hover": {
      bgcolor: active ? "rgba(31,93,107,0.12)" : "rgba(0,0,0,0.04)",
    },
  });
  const userManagementClientViewTooltip =
    "Client View: User Management is available for this user. Open User Management in normal mode to edit.";

  const [drawerOpen, setDrawerOpen] = useState(false);
  const closeDrawerAndNavigate = (path: string) => {
    setDrawerOpen(false);
    navigate(path);
  };

  if (!clientView.active || !userId) return null;

  const drawerContent = (
    <Box sx={{ width: 280, pt: 2, pb: 2 }} role="presentation">
      <List>
        <ListItemButton
          onClick={() => closeDrawerAndNavigate(basePath)}
          selected={isLandingPath}
          sx={{
            py: 1.25,
            "&.Mui-selected": { bgcolor: "rgba(31,93,107,0.08)" },
          }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <HomeIcon color={isLandingPath ? "primary" : "inherit"} />
          </ListItemIcon>
          <ListItemText
            primary="Home"
            primaryTypographyProps={{
              fontWeight: isLandingPath ? 800 : 600,
            }}
          />
        </ListItemButton>
        {clientView.isIntegrationUser && (
          <ListItemButton
            onClick={() => closeDrawerAndNavigate(`${basePath}/integrations`)}
            selected={isActive(`${basePath}/integrations`)}
            sx={{
              py: 1.25,
              "&.Mui-selected": { bgcolor: "rgba(31,93,107,0.08)" },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <TableChartIcon
                color={
                  isActive(`${basePath}/integrations`) ? "primary" : "inherit"
                }
              />
            </ListItemIcon>
            <ListItemText
              primary="Integrations"
              primaryTypographyProps={{
                fontWeight: isActive(`${basePath}/integrations`) ? 800 : 600,
              }}
            />
          </ListItemButton>
        )}
        {(clientView.isCalibrateAdmin || clientView.isSuperuser) && (
          <Tooltip title={userManagementClientViewTooltip}>
            <span>
              <ListItemButton disabled sx={{ py: 1.25 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <PeopleAltIcon />
                </ListItemIcon>
                <ListItemText
                  primary="User Management"
                  primaryTypographyProps={{ fontWeight: 600 }}
                />
              </ListItemButton>
            </span>
          </Tooltip>
        )}
        {clientView.isDataStorageUser && (
          <ListItemButton
            onClick={() => closeDrawerAndNavigate(storagePath)}
            selected={isDataStoragePath}
            sx={{
              py: 1.25,
              "&.Mui-selected": { bgcolor: "rgba(31,93,107,0.08)" },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <StorageIcon color={isDataStoragePath ? "primary" : "inherit"} />
            </ListItemIcon>
            <ListItemText
              primary="Data Storage"
              primaryTypographyProps={{
                fontWeight: isDataStoragePath ? 800 : 600,
              }}
            />
          </ListItemButton>
        )}
        {clientView.isChatBotAllowed && (
          <ListItemButton
            onClick={() => closeDrawerAndNavigate(`${basePath}/chat`)}
            selected={isActive(`${basePath}/chat`)}
            sx={{
              py: 1.25,
              "&.Mui-selected": { bgcolor: "rgba(31,93,107,0.08)" },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <ChatBubbleOutline
                color={isActive(`${basePath}/chat`) ? "primary" : "inherit"}
              />
            </ListItemIcon>
            <ListItemText
              primary="AI Agent"
              primaryTypographyProps={{
                fontWeight: isActive(`${basePath}/chat`) ? 800 : 600,
              }}
            />
          </ListItemButton>
        )}
        <Divider sx={{ my: 1 }} />
        <ListItemButton
          onClick={() => closeDrawerAndNavigate(`${basePath}/account-settings`)}
          selected={isActive(`${basePath}/account-settings`)}
          sx={{
            py: 1.25,
            "&.Mui-selected": { bgcolor: "rgba(31,93,107,0.08)" },
          }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <Settings />
          </ListItemIcon>
          <ListItemText
            primary="Account settings"
            primaryTypographyProps={{ fontWeight: 600 }}
          />
        </ListItemButton>
      </List>
    </Box>
  );

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        top: 0,
        bgcolor: "rgba(255,255,255,0.78)",
        backdropFilter: "blur(12px)",
        borderBottom: "1px solid rgba(0,0,0,0.08)",
        color: "text.primary",
      }}
    >
      <Toolbar sx={{ minHeight: { xs: 56, md: 80 }, gap: 2 }}>
        {isMobile && (
          <IconButton
            edge="start"
            color="inherit"
            aria-label="open menu"
            onClick={() => setDrawerOpen(true)}
            sx={{ mr: 0.5 }}
          >
            <MenuIcon />
          </IconButton>
        )}
        <Link to={basePath} style={{ display: "flex", flexShrink: 0 }}>
          <Box
            component="img"
            src={LOGO_URL}
            alt="Calibrate HCM"
            sx={{ height: { xs: 40, md: 56 }, my: 1, cursor: "pointer" }}
          />
        </Link>
        <Button
          size="small"
          startIcon={<ArrowBack />}
          onClick={onBackToUserManagement}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            color: "text.primary",
            flexShrink: 0,
          }}
        >
          {isMobile ? "Back" : "Back to User Management"}
        </Button>
        <Tooltip title="Read-only view as this client. You are not acting on their behalf.">
          <Chip
            label={`Client View · ${clientView.displayName || "this client"}`}
            size="small"
            sx={{
              height: 28,
              fontWeight: 600,
              bgcolor: "info.main",
              color: "info.contrastText",
              "& .MuiChip-label": { px: 1.25 },
              flexShrink: 0,
            }}
          />
        </Tooltip>
        <Box sx={{ flexGrow: 1 }} />

        {!isMobile && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {clientView.isIntegrationUser && (
              <>
                <Button
                  startIcon={<TableChartIcon />}
                  onClick={() => navigate(`${basePath}/integrations`)}
                  sx={navBtnSx(isActive(`${basePath}/integrations`))}
                >
                  Integrations
                </Button>
                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
              </>
            )}
            {(clientView.isCalibrateAdmin || clientView.isSuperuser) && (
              <>
                <Tooltip title={userManagementClientViewTooltip}>
                  <span>
                    <Button
                      startIcon={<PeopleAltIcon />}
                      disabled
                      sx={navBtnSx(false)}
                    >
                      User Management
                    </Button>
                  </span>
                </Tooltip>
                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
              </>
            )}
            {clientView.isDataStorageUser && (
              <>
                <Button
                  startIcon={<StorageIcon />}
                  onClick={() => navigate(storagePath)}
                  sx={navBtnSx(isDataStoragePath)}
                >
                  Data Storage
                </Button>
                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
              </>
            )}
            {clientView.isChatBotAllowed && (
              <>
                <Button
                  startIcon={<ChatBubbleOutline />}
                  onClick={() => navigate(`${basePath}/chat`)}
                  sx={navBtnSx(isActive(`${basePath}/chat`))}
                >
                  AI Agent
                </Button>
                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
              </>
            )}
            <Tooltip title="View this client's account settings (read-only)">
              <IconButton
                onClick={() => navigate(`${basePath}/account-settings`)}
                size="small"
                sx={{
                  ml: 0.25,
                  p: 0.5,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: isActive(`${basePath}/account-settings`)
                    ? "primary.main"
                    : "divider",
                  bgcolor: "background.paper",
                  "&:hover": { bgcolor: "rgba(0,0,0,0.03)" },
                }}
              >
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: 1.5,
                    display: "grid",
                    placeItems: "center",
                    fontSize: 13,
                    fontWeight: 900,
                    color: "primary.main",
                    bgcolor: "rgba(31,93,107,0.10)",
                  }}
                >
                  {clientView.displayName &&
                  clientView.displayName.trim().length > 0 ? (
                    clientView.displayName.trim()[0].toUpperCase()
                  ) : (
                    <AccountCircle fontSize="small" />
                  )}
                </Box>
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Toolbar>

      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{ "& .MuiDrawer-paper": { top: { xs: "56px", md: "80px" } } }}
      >
        {drawerContent}
      </Drawer>
    </AppBar>
  );
}
